import { apiClient } from '../axiosInstance';
import { ENDPOINTS } from '../endpoints';
import type { TenantBrandingConfig } from '../../stores/useTenantStore';
import type { TenantService } from './interfaces/TenantService';
import type { TenantConfigRaw, TenantCustomizationsRaw } from '../api-map';

function pick(f?: { value: string | null; default: string }): string {
  return f?.value ?? f?.default ?? '';
}

/**
 * Resolve the payment gateway from the tenant payload. Extracted to a
 * module-level helper so `init` stays under the cyclomatic-complexity ceiling.
 * 'stripe' fallback is dead code as of 2026-05 — no tenants use Stripe; kept
 * for legacy payload compatibility (PODZ-1655 surfaces it as a data anomaly).
 */
function resolveGatewayType(
  tenant: TenantConfigRaw,
): TenantBrandingConfig['gatewayType'] {
  if (tenant.stax) return 'stax';
  if (tenant.gpi) return 'gpi';
  return 'stripe';
}

export const tenantService: TenantService = {
  async init(subdomain) {
    const [tenantRes, customRes] = await Promise.all([
      apiClient.get<TenantConfigRaw>(ENDPOINTS.v1.tenants.config(subdomain)),
      apiClient.get<TenantCustomizationsRaw>(
        ENDPOINTS.v1.tenants.customizations(subdomain),
      ),
    ]);
    const tenant = tenantRes.data;
    const custom = customRes.data;
    const gatewayType = resolveGatewayType(tenant);
    const primary = pick(custom.Colors.primary_color);
    const secondary = pick(custom.Colors.secondary_color);
    return {
      primaryColor: primary,
      primaryHover: primary,
      primaryLight: primary,
      primaryText: '#ffffff',
      secondaryColor: secondary || primary,
      logoUrl: pick(custom.Assets.logo_url ?? custom.Assets.light_logo_url),
      faviconUrl: pick(custom.Assets.favicon_url),
      tenantPhone: pick(custom.General.phone),
      tenantSlug: tenant.subdomain,
      gatewayType,
      featureFlags: tenant.feature_flags ?? {},
      // IDEA-234 scheduling control (PODZ-1835). Top-level tenant boolean, NOT a
      // feature flag. Default-ON per AC6: a dropped/absent key must NOT disable a
      // configured-on tenant, so coalesce to `true` here (the store gate then
      // treats only an explicit `false` as off).
      allowSchedule: tenant.allow_schedule ?? true,
      // Appointment self-service controls (PODZ-1836). Same top-level-boolean,
      // default-ON treatment as `allowSchedule` — a dropped/absent key must NOT
      // disable a configured-on tenant, so coalesce to `true`.
      allowReschedule: tenant.allow_reschedule ?? true,
      allowCancel: tenant.allow_cancel ?? true,
      ga4TenantPropertyId: pick(custom.Analytics?.google_analytics_id) || null,
      supportEmail: tenant.support_email,
      tenantName: tenant.name,
      timezone: tenant.timezone,
      communicationDisclaimerNonMarketing: pick(
        custom.General.communication_disclaimer_non_marketing,
      ),
      communicationDisclaimerMarketing: pick(
        custom.General.communication_disclaimer_marketing,
      ),
      outOfCoverageMessage: pick(custom.General.out_of_coverage_message),
      // Invoice/quote footer customizations (Phase 5.2 D-17) — sourced from
      // the SAME customizations payload, no extra fetch.
      checkPayableTo: pick(custom.General.check_payable_to),
      licenseHtml: pick(custom.General.email_license_information),
      termsHtml: pick(custom.General.email_terms_of_service),
    };
  },
};
